import { useState, useCallback, useRef } from 'react'
import type {
  Workflow, Participant, Activity, Transition, WorkflowBinding,
  SelectedComponent, PanOffset, WorkflowSnapshot, ToolsConfig,
} from './types'
import { MAX_UNDO, DEFAULT_ACTIVITY_WIDTH, DEFAULT_ACTIVITY_HEIGHT, MIN_ZOOM, MAX_ZOOM, ZOOM_SPEED, DEFAULT_TOOLS_CONFIG } from './constants'
import { getWorkflowBounds } from './geometry'

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}

function getDefaultWorkflow(): Workflow {
  return {
    activities: [{
      id: 'start',
      type: 'start',
      x: 50,
      y: 50,
      width: DEFAULT_ACTIVITY_WIDTH,
      height: DEFAULT_ACTIVITY_HEIGHT,
      description: 'Start',
      performer: '',
      tool: null,
    }],
    transitions: [],
  }
}

export function useWorkflowState() {
  // Core data
  const [workflow, setWorkflow] = useState<Workflow>(getDefaultWorkflow())
  const [participants, setParticipants] = useState<Participant[]>([])
  const [objectName, setObjectName] = useState('')
  const [toolsConfig, setToolsConfig] = useState<ToolsConfig>(DEFAULT_TOOLS_CONFIG)
  // Approval binding (process-level TWASTA_* markers). Empty for a plain
  // (non-approval) workflow. The graphical editor always authors the
  // 'advanced' profile once an object is bound.
  const [binding, setBinding] = useState<WorkflowBinding>({})

  // UI state
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedType, setSelectedType] = useState<'activity' | 'transition' | null>(null)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [panOffset, setPanOffset] = useState<PanOffset>({ x: 0, y: 0 })
  const [transitionMode, setTransitionMode] = useState(false)
  const [transitionStartId, setTransitionStartId] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)

  // Refs for latest state (avoids stale closures)
  const workflowRef = useRef(workflow)
  workflowRef.current = workflow
  const participantsRef = useRef(participants)
  participantsRef.current = participants
  const objectNameRef = useRef(objectName)
  objectNameRef.current = objectName
  const bindingRef = useRef(binding)
  bindingRef.current = binding

  // ID counters (monotonically increasing, never reset on delete)
  const activityCounterRef = useRef(1) // start has index 0
  const transitionCounterRef = useRef(0)

  const nextActivityId = useCallback(() => `activity_${activityCounterRef.current++}`, [])
  const nextTransitionId = useCallback(() => `transition_${transitionCounterRef.current++}`, [])

  // Undo/Redo stacks in refs
  const undoStackRef = useRef<WorkflowSnapshot[]>([{
    workflow: deepClone(getDefaultWorkflow()),
    participants: [],
    binding: {},
  }])
  const redoStackRef = useRef<WorkflowSnapshot[]>([])
  const [undoCount, setUndoCount] = useState(0)
  const [redoCount, setRedoCount] = useState(0)

  // ---- Helpers ----

  const getSelected = useCallback((): SelectedComponent => {
    if (!selectedId || !selectedType) return null
    if (selectedType === 'activity') {
      return workflowRef.current.activities.find(a => a.id === selectedId) || null
    }
    return workflowRef.current.transitions.find(t => t.id === selectedId) || null
  }, [selectedId, selectedType])

  const selectComponent = useCallback((component: SelectedComponent) => {
    if (!component) {
      setSelectedId(null)
      setSelectedType(null)
      return
    }
    if ('from' in component && 'to' in component) {
      setSelectedId(component.id)
      setSelectedType('transition')
    } else {
      setSelectedId((component as Activity).id)
      setSelectedType('activity')
    }
  }, [])

  // ---- State snapshots (undo/redo) ----

  const saveState = useCallback(() => {
    const snapshot: WorkflowSnapshot = {
      workflow: deepClone(workflowRef.current),
      participants: deepClone(participantsRef.current),
      binding: deepClone(bindingRef.current),
    }
    // Skip if identical to last snapshot
    const last = undoStackRef.current[undoStackRef.current.length - 1]
    if (last && JSON.stringify(snapshot) === JSON.stringify(last)) return

    undoStackRef.current.push(snapshot)
    if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift()
    redoStackRef.current = []
    setUndoCount(undoStackRef.current.length - 1)
    setRedoCount(0)
    setIsDirty(true)
  }, [])

  const undo = useCallback(() => {
    if (undoStackRef.current.length <= 1) return
    const current: WorkflowSnapshot = {
      workflow: deepClone(workflowRef.current),
      participants: deepClone(participantsRef.current),
      binding: deepClone(bindingRef.current),
    }
    redoStackRef.current.push(current)
    undoStackRef.current.pop()
    const prev = undoStackRef.current[undoStackRef.current.length - 1]
    setWorkflow(deepClone(prev.workflow))
    setParticipants(deepClone(prev.participants))
    setBinding(deepClone(prev.binding ?? {}))
    setSelectedId(null)
    setSelectedType(null)
    setUndoCount(undoStackRef.current.length - 1)
    setRedoCount(redoStackRef.current.length)
  }, [])

  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) return
    const next = redoStackRef.current.pop()!
    undoStackRef.current.push(deepClone(next))
    setWorkflow(deepClone(next.workflow))
    setParticipants(deepClone(next.participants))
    setBinding(deepClone(next.binding ?? {}))
    setSelectedId(null)
    setSelectedType(null)
    setUndoCount(undoStackRef.current.length - 1)
    setRedoCount(redoStackRef.current.length)
  }, [])

  // ---- Load / reset ----

  const loadState = useCallback((wf: Workflow, parts: Participant[], name: string, tools?: ToolsConfig, bind?: WorkflowBinding) => {
    setWorkflow(wf)
    setParticipants(parts)
    setObjectName(name)
    const loadedBinding: WorkflowBinding = bind ? { ...bind } : {}
    // Once an object is bound, the graphical editor only authors 'advanced'.
    if (loadedBinding.bindObject) loadedBinding.profile = 'advanced'
    setBinding(loadedBinding)
    if (tools?.Tools?.Tool?.length) {
      // Merge XPDL-parsed tools with defaults (XPDL tools take priority for same Id)
      // Carry over Icon from defaults when XPDL tool doesn't define one
      const defaultTools = DEFAULT_TOOLS_CONFIG.Tools?.Tool || []
      const defaultMap = new Map(defaultTools.map(t => [t.Id, t]))
      const xpdlIds = new Set(tools.Tools.Tool.map(t => t.Id))
      const enriched = tools.Tools.Tool.map(t => {
        const def = defaultMap.get(t.Id)
        if (def?.Icon && !t.Icon) return { ...t, Icon: def.Icon }
        return t
      })
      const merged = [...enriched, ...defaultTools.filter(t => !xpdlIds.has(t.Id))]
      setToolsConfig({ Tools: { Tool: merged } })
    }
    // Sync ID counters to avoid collisions with loaded IDs
    let maxAct = 0, maxTrans = 0
    for (const a of wf.activities) {
      const m = a.id.match(/^activity_(\d+)$/)
      if (m) maxAct = Math.max(maxAct, parseInt(m[1]) + 1)
    }
    for (const t of wf.transitions) {
      const m = t.id.match(/^transition_(\d+)$/)
      if (m) maxTrans = Math.max(maxTrans, parseInt(m[1]) + 1)
    }
    activityCounterRef.current = Math.max(activityCounterRef.current, maxAct)
    transitionCounterRef.current = Math.max(transitionCounterRef.current, maxTrans)
    setSelectedId(null)
    setSelectedType(null)
    setTransitionMode(false)
    setTransitionStartId(null)
    setIsDirty(false)
    undoStackRef.current = [{ workflow: deepClone(wf), participants: deepClone(parts), binding: deepClone(loadedBinding) }]
    redoStackRef.current = []
    setUndoCount(0)
    setRedoCount(0)
  }, [])

  // ---- Activity CRUD ----

  const addActivity = useCallback((x?: number, y?: number) => {
    const wf = workflowRef.current
    const lastAct = wf.activities[wf.activities.length - 1]
    const newX = x ?? (lastAct ? lastAct.x + 150 : 200)
    const newY = y ?? (lastAct ? lastAct.y : 50)
    const id = nextActivityId()

    const newAct: Activity = {
      id,
      type: 'activity',
      x: newX,
      y: newY,
      width: DEFAULT_ACTIVITY_WIDTH,
      height: DEFAULT_ACTIVITY_HEIGHT,
      description: 'New Activity',
      performer: '',
      tool: { type: 'Application', name: '', parameters: [] },
      icon: '',
    }

    const newActivities = [...wf.activities, newAct]
    const newTransitions = [...wf.transitions]

    // Auto-connect from last non-end activity
    if (lastAct && lastAct.type !== 'end') {
      const exists = newTransitions.some(t => t.from === lastAct.id && t.to === id)
      if (!exists) {
        newTransitions.push({
          id: nextTransitionId(),
          from: lastAct.id,
          to: id,
          condition: '',
          description: '',
        })
      }
    }

    setWorkflow({ activities: newActivities, transitions: newTransitions })
    saveState()
    selectComponent(newAct)
    return newAct
  }, [saveState, selectComponent, nextActivityId, nextTransitionId])

  const addEndActivity = useCallback(() => {
    const wf = workflowRef.current
    const endCount = wf.activities.filter(a => a.type === 'end').length
    const endId = endCount === 0 ? 'end' : `end_${endCount + 1}`
    const lastAct = wf.activities[wf.activities.length - 1]

    const endAct: Activity = {
      id: endId,
      type: 'end',
      x: lastAct ? lastAct.x + 150 : 350,
      y: lastAct ? lastAct.y : 50,
      width: DEFAULT_ACTIVITY_WIDTH,
      height: DEFAULT_ACTIVITY_HEIGHT,
      description: endId === 'end' ? 'End' : `End ${endCount + 1}`,
      performer: '',
      tool: null,
      // No endKind by default — a plain (non-approval) workflow then serialises
      // byte-identically to the legacy output (no TWASTA_END). The engine treats
      // an end with no TWASTA_END as 'approved', and the End-result selector
      // lets the user mark an explicit approved/rejected outcome when needed.
    }

    const newActivities = [...wf.activities, endAct]
    const newTransitions = [...wf.transitions]

    if (lastAct && lastAct.type !== 'end') {
      newTransitions.push({
        id: nextTransitionId(),
        from: lastAct.id,
        to: endId,
        condition: '',
        description: '',
      })
    }

    setWorkflow({ activities: newActivities, transitions: newTransitions })
    saveState()
    return endAct
  }, [saveState, nextTransitionId])

  const updateActivity = useCallback((activityId: string, updates: Partial<Activity>) => {
    setWorkflow(prev => {
      const newActivities = prev.activities.map(a => {
        if (a.id !== activityId) return a
        const updated = { ...a, ...updates }
        // If ID changed, update transitions
        if (updates.id && updates.id !== activityId) {
          prev.transitions.forEach(t => {
            if (t.from === activityId) t.from = updates.id!
            if (t.to === activityId) t.to = updates.id!
          })
        }
        return updated
      })
      return { ...prev, activities: newActivities }
    })
  }, [])

  const moveActivity = useCallback((activityId: string, x: number, y: number) => {
    setWorkflow(prev => ({
      ...prev,
      activities: prev.activities.map(a =>
        a.id === activityId ? { ...a, x, y } : a
      ),
    }))
  }, [])

  const deleteComponent = useCallback((component: SelectedComponent) => {
    if (!component) return
    const wf = workflowRef.current

    if ('from' in component && 'to' in component) {
      // Delete transition
      setWorkflow({
        ...wf,
        transitions: wf.transitions.filter(t => t.id !== component.id),
      })
    } else {
      const act = component as Activity
      if (act.type === 'start') return // Cannot delete start
      setWorkflow({
        activities: wf.activities.filter(a => a.id !== act.id),
        transitions: wf.transitions.filter(t => t.from !== act.id && t.to !== act.id),
      })
    }

    setSelectedId(null)
    setSelectedType(null)
    saveState()
  }, [saveState])

  const duplicateActivity = useCallback((activityId: string) => {
    const wf = workflowRef.current
    const orig = wf.activities.find(a => a.id === activityId)
    if (!orig || orig.type === 'start') return

    const clone: Activity = {
      ...deepClone(orig),
      id: nextActivityId(),
      x: orig.x + 50,
      y: orig.y + 50,
    }

    setWorkflow({ ...wf, activities: [...wf.activities, clone] })
    saveState()
  }, [saveState, nextActivityId])

  // ---- Transition CRUD ----

  const addTransition = useCallback((fromId: string, toId: string): boolean => {
    const wf = workflowRef.current
    if (wf.transitions.some(t => t.from === fromId && t.to === toId)) return false

    const newT: Transition = {
      id: nextTransitionId(),
      from: fromId,
      to: toId,
      condition: '',
      description: '',
    }

    setWorkflow({ ...wf, transitions: [...wf.transitions, newT] })
    saveState()
    return true
  }, [saveState, nextTransitionId])

  const updateTransition = useCallback((transitionId: string, updates: Partial<Transition>) => {
    setWorkflow(prev => ({
      ...prev,
      transitions: prev.transitions.map(t =>
        t.id === transitionId ? { ...t, ...updates } : t
      ),
    }))
  }, [])

  // ---- Transition mode ----

  const toggleTransitionMode = useCallback(() => {
    setTransitionMode(prev => !prev)
    setTransitionStartId(null)
  }, [])

  const handleTransitionModeClick = useCallback((activityId: string) => {
    if (!transitionStartId) {
      setTransitionStartId(activityId)
    } else {
      addTransition(transitionStartId, activityId)
      setTransitionStartId(null)
      setTransitionMode(false)
    }
  }, [transitionStartId, addTransition])

  const cancelTransitionMode = useCallback(() => {
    setTransitionMode(false)
    setTransitionStartId(null)
  }, [])

  // ---- Zoom/pan ----

  const zoomIn = useCallback(() => {
    setZoomLevel(z => Math.min(MAX_ZOOM, z + ZOOM_SPEED))
  }, [])

  const zoomOut = useCallback(() => {
    setZoomLevel(z => Math.max(MIN_ZOOM, z - ZOOM_SPEED))
  }, [])

  const zoomFit = useCallback((canvasWidth: number, canvasHeight: number) => {
    const bounds = getWorkflowBounds(workflowRef.current)
    if (!bounds) return
    const padding = 50
    const scaleX = (canvasWidth - padding * 2) / bounds.width
    const scaleY = (canvasHeight - padding * 2) / bounds.height
    const newZoom = Math.min(1, Math.min(scaleX, scaleY))
    setZoomLevel(newZoom)
    // Center the workflow in the canvas
    setPanOffset({
      x: (canvasWidth - bounds.width * newZoom) / 2 - bounds.x * newZoom,
      y: (canvasHeight - bounds.height * newZoom) / 2 - bounds.y * newZoom,
    })
  }, [])

  const zoomToward = useCallback((newZoom: number, mouseX: number, mouseY: number) => {
    setZoomLevel(prev => {
      const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, newZoom))
      if (clamped === prev) return prev
      const factor = clamped / prev
      setPanOffset(p => ({
        x: mouseX - factor * (mouseX - p.x),
        y: mouseY - factor * (mouseY - p.y),
      }))
      return clamped
    })
  }, [])

  // ---- Alignment ----

  const alignActivity = useCallback((activityId: string, alignment: 'left' | 'center' | 'right') => {
    const bounds = getWorkflowBounds(workflowRef.current)
    if (!bounds) return
    setWorkflow(prev => ({
      ...prev,
      activities: prev.activities.map(a => {
        if (a.id !== activityId) return a
        let x = a.x
        if (alignment === 'left') x = bounds.x
        else if (alignment === 'center') x = bounds.x + (bounds.width - a.width) / 2
        else if (alignment === 'right') x = bounds.x + bounds.width - a.width
        return { ...a, x }
      }),
    }))
    saveState()
  }, [saveState])

  // ---- Participants ----

  const addParticipant = useCallback((participant: Participant) => {
    setParticipants(prev => [...prev, participant])
    saveState()
  }, [saveState])

  const updateParticipant = useCallback((index: number, participant: Participant) => {
    setParticipants(prev => {
      const next = [...prev]
      next[index] = participant
      return next
    })
    saveState()
  }, [saveState])

  const deleteParticipant = useCallback((index: number): boolean => {
    const p = participantsRef.current[index]
    if (!p) return false
    // Check if used
    if (workflowRef.current.activities.some(a => a.performer === p.id)) return false
    setParticipants(prev => prev.filter((_, i) => i !== index))
    saveState()
    return true
  }, [saveState])

  // ---- Tool helpers ----

  const getDistinctToolTypes = useCallback((): string[] => {
    if (!toolsConfig?.Tools?.Tool) return []
    const types = new Set<string>()
    toolsConfig.Tools.Tool.forEach(t => {
      if (t.Type) types.add(t.Type)
    })
    return Array.from(types)
  }, [toolsConfig])

  const getToolsByType = useCallback((toolType: string) => {
    if (!toolsConfig?.Tools?.Tool) return []
    return toolsConfig.Tools.Tool.filter(t => (t.Type || 'Application') === toolType)
  }, [toolsConfig])

  const getToolDefinition = useCallback((toolId: string) => {
    return toolsConfig?.Tools?.Tool.find(t => t.Id === toolId)
  }, [toolsConfig])

  // ---- Approval binding ----

  // Set (or clear, with '') the bound transaction object. Binding to an object
  // forces the 'advanced' profile (the graphical editor never authors linear).
  // Clearing the object drops the binding entirely so the workflow saves as a
  // plain (non-approval) one again.
  const setBindObject = useCallback((objName: string) => {
    setBinding(prev => {
      if (!objName) {
        // Unbind → no approval markers at all.
        if (!prev.bindObject && !prev.profile && !(prev.bindTriggers?.length)) return prev
        return {}
      }
      return { ...prev, bindObject: objName, profile: 'advanced' }
    })
    saveState()
  }, [saveState])

  const setBindTriggers = useCallback((triggers: string[]) => {
    const norm = triggers.map(t => t.trim().toLowerCase()).filter(Boolean)
    setBinding(prev => ({ ...prev, bindTriggers: norm }))
    saveState()
  }, [saveState])

  return {
    // Data
    workflow,
    participants,
    objectName,
    setObjectName,

    // Selection
    selectedId,
    selectedType,
    getSelected,
    selectComponent,

    // Undo/redo
    undoCount,
    redoCount,
    undo,
    redo,
    saveState,

    // Load
    loadState,
    isDirty,
    setIsDirty,

    // Activity CRUD
    addActivity,
    addEndActivity,
    updateActivity,
    moveActivity,
    deleteComponent,
    duplicateActivity,

    // Transition CRUD
    addTransition,
    updateTransition,

    // Transition mode
    transitionMode,
    transitionStartId,
    toggleTransitionMode,
    handleTransitionModeClick,
    cancelTransitionMode,

    // Zoom/pan
    zoomLevel,
    setZoomLevel,
    panOffset,
    setPanOffset,
    zoomIn,
    zoomOut,
    zoomFit,
    zoomToward,

    // Alignment
    alignActivity,

    // Participants
    addParticipant,
    updateParticipant,
    deleteParticipant,

    // Tool helpers
    toolsConfig,
    setToolsConfig,
    getDistinctToolTypes,
    getToolsByType,
    getToolDefinition,

    // Approval binding
    binding,
    setBindObject,
    setBindTriggers,

    // Refs
    workflowRef,
    participantsRef,
    objectNameRef,
    bindingRef,
  }
}
